import Big from 'big.js';

// Pure display-decimals core shared (by copy) with wallet-react-native — keep
// the two files identical. See DISPLAY_DECIMALS_SPEC.md for the full model.
//
// Amounts render with min/max fraction digits: truncated (round-down) to `max`,
// trailing zeros stripped but never below `min`. `max` comes from company
// config (`settings.display_decimals`), `min` is 2 so money still looks like
// money. The exact preset raises `max` to the currency's divisibility so no
// digit the user committed is ever hidden.

const DEFAULT_CONFIG = { default: 2, crypto: 4, overrides: {} };

// Module-level store so the pure formatting helpers — also called from
// non-React util modules — can read config without threading it through every
// call site. Synced from company settings at app load.
let config = { ...DEFAULT_CONFIG };

const clampCap = n => {
  if (typeof n !== 'number' || isNaN(n)) return null;
  return Math.min(20, Math.max(0, Math.floor(n)));
};

export const setDisplayDecimalsConfig = value => {
  if (value == null) return;
  if (typeof value === 'number') {
    const cap = clampCap(value);
    if (cap != null) config = { ...DEFAULT_CONFIG, default: cap };
    return;
  }
  if (typeof value === 'object') {
    const next = { ...DEFAULT_CONFIG };
    const defaultCap = clampCap(value.default);
    const cryptoCap = clampCap(value.crypto);
    if (defaultCap != null) next.default = defaultCap;
    if (cryptoCap != null) next.crypto = cryptoCap;
    if (value.overrides && typeof value.overrides === 'object') {
      next.overrides = Object.entries(value.overrides).reduce(
        (acc, [code, cap]) => {
          const clamped = clampCap(cap);
          if (clamped != null) acc[code] = clamped;
          return acc;
        },
        {},
      );
    }
    config = next;
  }
};

export const getDisplayDecimalsConfig = () => config;

// Best-effort crypto detection: web sets `currency.crypto` from
// metadata.native_context (util/crypto.js); fall back to the metadata itself.
const isCryptoCurrency = currency =>
  Boolean(currency?.crypto || currency?.metadata?.native_context?.crypto);

const resolveCap = currency => {
  const code = currency?.code;
  if (code != null && config.overrides[code] != null) {
    return config.overrides[code];
  }
  if (isCryptoCurrency(currency)) return config.crypto;
  return config.default;
};

// Resolve {min, max} fraction digits for a currency. Pass `divisibility` when
// the caller only has the raw number (legacy signatures); it wins over the
// currency object's divisibility.
export const getDisplayDecimals = (
  currency,
  { exact = false, divisibility } = {},
) => {
  const d =
    typeof divisibility === 'number'
      ? divisibility
      : typeof currency?.divisibility === 'number'
        ? currency.divisibility
        : 2;
  const max = exact ? d : Math.min(resolveCap(currency), d);
  const min = Math.min(2, d, max);
  return { min, max };
};

const stripTrailingZeros = (fixed, min) => {
  const dotIndex = fixed.indexOf('.');
  if (dotIndex === -1) return fixed;
  const int = fixed.slice(0, dotIndex);
  const frac = fixed.slice(dotIndex + 1);
  const trimmed = frac.replace(/0+$/, '');
  const kept = trimmed.length >= min ? trimmed : frac.slice(0, min);
  return kept.length ? `${int}.${kept}` : int;
};

// Smallest value representable at `max` decimals, e.g. 2 -> "0.01", 0 -> "1".
const smallestUnit = max =>
  max > 0 ? `0.${'0'.repeat(max - 1)}1` : '1';

// Core formatter: returns a bare numeric string (no commas, no currency code).
// With `dustGuard` (display-only callers!) a nonzero amount that truncates to
// zero renders as "< 0.01"-style instead of a misleading "0.00". Legacy
// callers that only have a raw divisibility pass it via opts.divisibility.
export const formatDecimalValue = (
  amount,
  currency,
  {
    exact = false,
    dustGuard = false,
    withDivisibility = false,
    floor = true,
    divisibility,
  } = {},
) => {
  const d =
    typeof divisibility === 'number'
      ? divisibility
      : typeof currency?.divisibility === 'number'
        ? currency.divisibility
        : 2;
  const { min, max } = getDisplayDecimals(currency, {
    exact,
    divisibility: d,
  });
  let bn = Big(amount || 0);
  if (withDivisibility && d > 0) bn = bn.div(Big(10).pow(d));
  // Numeric rounding modes (0 = down/truncate, 1 = half-up) so the file works
  // on both big.js v5 (mobile) and v6+ (web); v5 has no Big.roundDown and its
  // toFixed takes no rounding-mode argument.
  const fixed = bn.round(max, floor ? 0 : 1).toFixed(max);
  const result = stripTrailingZeros(fixed, min);
  if (dustGuard && !bn.eq(0) && Big(result).eq(0)) {
    return `< ${smallestUnit(max)}`;
  }
  return result;
};
